// src/pages/History/History.js
import React, { useEffect, useState } from "react";
import { Card, Button, Container, Col, Row, Spinner, Badge } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { TRANSACTION_HISTORY } from "../../services/actions";
import { 
  CalendarDots, Clock, CurrencyInr, Receipt,
  CheckCircle, XCircle, WarningCircle, CaretDown, ArrowLeft
} from "@phosphor-icons/react";
import AppLayout from "../../layouts/AppLayout";

export default function History() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const paymentState = useSelector((state) => state?.paymentState);
  
  const getUserData = () => {
    const selectedCustomer = localStorage.getItem("selected_customer_data");
    if (selectedCustomer) {
      try {
        const parsed = JSON.parse(selectedCustomer);
        if (parsed.customer_id) {
          return {
            customerID: parsed.customer_id,
            customerName: parsed.customer_name,
            cellNumber: parsed.customer_phone,
          };
        }
      } catch (e) {
        console.error("Error parsing selected_customer_data:", e);
      }
    }

    const userData = localStorage.getItem("data");
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }

    return {};
  };

  const user = getUserData();
  const [allTransactions, setAllTransactions] = useState([]);
  const [displayCount, setDisplayCount] = useState(20);
  const [loading, setLoading] = useState(true);

  // ✅ FETCH TRANSACTION HISTORY
  useEffect(() => {
    if (!user?.customerID) {
      console.error("❌ No customer ID found!");
      setLoading(false);
      return;
    }

    console.log("📜 Fetching transaction history for:", user.customerID);
    setLoading(true);
    
    dispatch({
      type: `${TRANSACTION_HISTORY}_GET`,
      payload: `transaction_history/${user.customerID}/`,
      callback: (resp) => {
        console.log("✅ [CALLBACK] Response:", resp);
        
        const results = resp?.data?.results || [];
        
        if (results.length > 0) {
          console.log("✅ Found", results.length, "transactions");
          
          const processedTransactions = results.map(row => {
            const date = new Date(row.created_at);
            return {
              ...row,
              sortDate: date,
              monthYear: date.toLocaleString("en-US", { year: "numeric", month: "short" }),
              day: date.toLocaleString("en-US", { day: "2-digit" }),
              time: date.toLocaleString("en-US", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              }),
            };
          }).sort((a, b) => b.sortDate - a.sortDate);
          
          setAllTransactions(processedTransactions);
          setLoading(false);
        } else if (resp?.error) {
          console.error("❌ Error:", resp.message);
          setAllTransactions([]);
          setLoading(false);
        } else {
          console.log("⚠️ No transactions found");
          setAllTransactions([]);
          setLoading(false);
        }
      }
    });

    const timeout = setTimeout(() => {
      console.log("⏰ Force stop loading");
      setLoading(false);
    }, 10000);

    return () => clearTimeout(timeout);
  }, [dispatch, user?.customerID]);

  // ✅ FALLBACK: Process from Redux if callback failed
  useEffect(() => {
    if (allTransactions.length > 0) return;
    
    const results = paymentState?.data?.data?.results || [];
    
    if (results.length > 0) {
      console.log("📊 Processing from Redux:", results.length, "transactions");
      
      const processedTransactions = results.map(row => {
        const date = new Date(row.created_at);
        return {
          ...row,
          sortDate: date,
          monthYear: date.toLocaleString("en-US", { year: "numeric", month: "short" }),
          day: date.toLocaleString("en-US", { day: "2-digit" }),
          time: date.toLocaleString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }),
        };
      }).sort((a, b) => b.sortDate - a.sortDate);

      setAllTransactions(processedTransactions);
      setLoading(false);
    }
  }, [paymentState?.data, allTransactions.length]);

  const visibleTransactions = allTransactions.slice(0, displayCount);
  const hasMore = displayCount < allTransactions.length;

  const groupedTransactions = visibleTransactions.reduce((groups, txn) => {
    const monthKey = txn.monthYear;
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(txn);
    return groups;
  }, {});

  const getStatusConfig = (status) => {
    const configs = {
      PENDING: { text: "Pending", variant: "warning", icon: <Clock size={14} weight="fill" /> },
      SUCCESS: { text: "Success", variant: "success", icon: <CheckCircle size={14} weight="fill" /> },
      USER_DROPPED: { text: "User Dropped", variant: "primary", icon: <WarningCircle size={14} weight="fill" /> },
      FAILED: { text: "Failed", variant: "danger", icon: <XCircle size={14} weight="fill" /> },
    };
    return configs[status] || { text: status, variant: "secondary", icon: null };
  };

  const handleViewReceipt = (row) => {
    const payload = {
      order_id: row.order_id,
      cf_payment_id: row.cf_payment_id || "",
      reciept_no: row.reciept_no || "",
      payment_amount: row.payment_amount || "",
      customer_note: row.customer_note || "",
      payment_completion_time: row.modified_date,
      payment_time: row.time,
      payment_status: row.payment_status,
      payment_message: row.payment_message || "",
      loan_number: row.loan_number,
      gateway: row.gateway || "",
    };
    navigate(`/receipt?od=${btoa(JSON.stringify(payload))}`);
  };

  const handleLoadMore = () => setDisplayCount(prev => prev + 20);

  if (!user?.customerID) {
    return (
      <AppLayout>
        <Container className="px-3 mt-5">
          <Row className="justify-content-center">
            <Col xs={12} md={8}>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-5 text-center">
                  <XCircle size={64} className="text-danger mb-3" />
                  <h5 className="mb-3">Customer ID Not Found</h5>
                  <p className="text-muted mb-4">Please log in again.</p>
                  <Button variant="primary" onClick={() => navigate("/dashboard/")}>
                    <ArrowLeft size={16} className="me-2" />Back
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </AppLayout>
    );
  }

  if (loading && allTransactions.length === 0) {
    return (
      <AppLayout>
        <Container className="px-3 mt-5">
          <Row className="justify-content-center">
            <Col xs={12} md={8}>
              <Card className="shadow-sm border-0">
                <Card.Body className="p-5 text-center">
                  <Spinner animation="border" variant="primary" className="mb-3" />
                  <h5 className="mb-2">Loading Transaction History...</h5>
                  <p className="text-muted mb-0">Customer: {user.customerID}</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="app-container">
        <div className="d-flex flex-column pb-5" style={{ minHeight: "100vh" }}>
          <div className="d-flex justify-content-between align-items-center mx-4 mt-5 flex-wrap gap-3">
            <div>
              <h1 className="mb-2 app-title">Payment Portal</h1>
              <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                {user.customerName && `${user.customerName} • `}
                Showing {visibleTransactions.length} of {allTransactions.length} transactions
              </p>
            </div>
            <Button variant="outline-primary" size="sm" onClick={() => navigate("/dashboard/")} className="d-flex align-items-center gap-2">
              <ArrowLeft size={18} /><span>Back</span>
            </Button>
          </div>

          <div className="d-flex gap-4 justify-content-center w-100 mt-4 mb-4">
            <h4 className="text-muted d-flex align-items-center gap-2">
              <Receipt size={24} weight="duotone" />Transaction History
            </h4>
          </div>

          {allTransactions.length === 0 && (
            <Container className="w-100 mt-5">
              <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6}>
                  <Card className="shadow-sm border-0">
                    <Card.Body className="p-5 text-center">
                      <Receipt size={64} className="text-muted mb-3" weight="light" />
                      <h5 className="mb-3">No Transactions Found</h5>
                      <p className="text-muted">You haven't made any transactions yet.</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          )}

          {Object.keys(groupedTransactions).sort((a, b) => new Date(b) - new Date(a)).map((monthKey) => (
            <Container key={monthKey} className="w-100 mb-4 py-4" style={{ paddingBottom: '100px' }}>
              <div className="mb-3 pb-2 border-bottom">
                <h5 className="mb-0 text-primary">{monthKey}</h5>
                <small className="text-muted">{groupedTransactions[monthKey].length} transaction(s)</small>
              </div>

              {groupedTransactions[monthKey].map((row) => {
                const statusConfig = getStatusConfig(row.payment_status);
                return (
                  <Card key={row.order_id} className="mb-3 shadow-sm border-0" style={{ cursor: "pointer", transition: "all 0.2s ease" }}
                    onClick={() => handleViewReceipt(row)}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = ""; }}>
                    <Card.Body className="p-3">
                      <Row className="align-items-center g-2">
                        <Col xs={12} md={6}>
                          <div className="mb-1"><small className="text-muted">Order ID : </small><div className="fw-semibold" style={{ fontSize: '14px' }}>{row.order_id}</div></div>
                          <div className="mb-1"><small className="text-muted">Loan No : </small><div style={{ fontSize: '14px' }}>{row.loan_number}</div></div>
                           <div className="mb-1"><small className="text-muted">Receipt No : </small><div style={{ fontSize: '14px' }}>{row.reciept_no}</div></div>
                            <div className="mb-1"><small className="text-muted">Reference No : </small><div style={{ fontSize: '14px' }}>{row.bank_reference}</div></div>
                          <div className="d-flex align-items-center gap-2 text-muted"><CalendarDots size={16} /><span style={{ fontSize: '13px' }}>{row.day}</span><Clock size={16} /><span style={{ fontSize: '13px' }}>{row.time}</span></div>
                          {/* {row.gateway && <div className="mt-1"><small className="text-muted">Gateway: <strong>{row.gateway}</strong></small></div>} */}
                        </Col>
                        <Col xs={6} md={3} className="text-md-center">
                          <div className="mb-2"><small className="text-muted d-block">Amount</small><div className="fw-bold d-flex align-items-center justify-content-md-center"><CurrencyInr size={18} /><span>{parseFloat(row.payment_amount).toLocaleString('en-IN')}</span></div></div>
                          <Badge bg={statusConfig.variant} className="d-inline-flex align-items-center gap-1 px-2 py-1">{statusConfig.icon}<span style={{ fontSize: '12px' }}>{statusConfig.text}</span></Badge>
                        </Col>
                        <Col xs={6} md={3} className="text-end">
                          <Button variant="outline-primary" size="sm" onClick={(e) => { e.stopPropagation(); handleViewReceipt(row); }}><Receipt size={16} className="me-1" />Receipt</Button>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                );
              })}
            </Container>
          ))}

          {hasMore && (
            <Container className="w-100 mb-4 ">
              <div className="text-center">
                <Button variant="primary" size="lg" onClick={handleLoadMore} className="px-5" style={{ borderRadius: '25px', fontWeight: '600' }}>
                  <CaretDown size={20} weight="bold" className="me-2" />View More
                  <span className="ms-2 badge bg-light text-primary">+{Math.min(20, allTransactions.length - displayCount)}</span>
                </Button>
              </div>
            </Container>
          )}

          {!hasMore && allTransactions.length > 20 && (
            <Container className="w-100 py-6" style={{ paddingBottom: '100px' }}>
              <div className="text-center">
                <p className="text-muted mb-4"><CheckCircle size={18} weight="fill" className="me-2 text-success" />All {allTransactions.length} transactions loaded</p>
              </div>
            </Container>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
